import { faSave } from "@fortawesome/free-solid-svg-icons";
import { useModalControls } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { FC, useCallback, useState } from "react";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useRefresh } from "../../../lib/src/components/RefreshController";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { MailDomain, TransactionalMailDomainDeploymentDetails } from "../../../lib/src/api/partner/models/MailDomain";
import { Button, CopyableInput, Input } from "@ventumsoftware/react-toolkit";
import { Paragraph } from "../../../lib/src/components/Paragraph";

export const AddTransactionalMailUserModal: FC<{ domain: MailDomain }> = ({ domain }) => {
  const { partnerClient } = useApiClient(config)
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const refresh = useRefresh()
  const { close } = useModalControls()
  const deploymentDetails = domain.deploymentDetails instanceof TransactionalMailDomainDeploymentDetails ? domain.deploymentDetails : null
  const emailDomain = deploymentDetails?.domain ?? domain.displayName
  const [localPart, setLocalPart] = useState('')
  const [error, setError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [smtpPassword, setSmtpPassword] = useState<string | null>(null)

  const save = useCallback(async () => {
    setIsSaving(true)
    try {
      const email = `${localPart}@${emailDomain}`
      const result = await partnerClient.addUserToTransactionalMailDomain(domain.id, email)
      if (!result.success) {
        setError(result.error)
        return
      }
      await awaitNextDeploymentFinished(result.data.deployment.id)
      refresh()
      setSmtpPassword(result.data.smtpPassword)
    } finally {
      setIsSaving(false)
    }
  }, [domain, emailDomain, localPart])

  if (smtpPassword !== null) {
    return <>
      <div className={"space-y-4 flex-1"}>
        <Paragraph>The SMTP password below is shown only once. Copy it now.</Paragraph>
        <CopyableInput label={"SMTP password"} value={smtpPassword} />
      </div>
      <footer className={"-mx-4 px-4 mt-3 py-3 border-t border-slate-100 flex items-center space-x-4"}>
        <Button type={'primary'} size={'md'} text={'Done'} onClick={() => close()} />
      </footer>
    </>
  }

  return <>
    <form onSubmit={(e) => { e.preventDefault(); save() }} className={"space-y-4 flex-1"}>
      <div className={"flex items-end"}>
        <Input label={'Email address'} type={"text"} value={localPart} onChange={setLocalPart} />
        <div className={"h-10 flex items-center font-medium text-slate-800 ml-2"}>@{emailDomain}</div>
      </div>
      {error.length > 0 && <div className={"font-medium text-red-600"}>
        {error}
      </div>}
    </form>
    <footer className={"-mx-4 px-4 mt-3 py-3 border-t border-slate-100 flex items-center space-x-4"}>
      <Button type={'primary'} disabled={isSaving || localPart.length === 0} size={'md'} icon={faSave} text={isSaving ? 'Saving...' : 'Add'} onClick={save} />
      <Button type={'secondary'} size={'md'} text={'Cancel'} onClick={() => close()} />
    </footer>
  </>
}
